import { useRef } from "react";
import { Link } from "react-router-dom";
import {
    motion,
    useMotionValue,
    useSpring,
    useTransform,
    useScroll,
} from "framer-motion";
import {
    ArrowUpRight,
    Phone,
    Users,
    BookOpenCheck,
    ClipboardCheck,
    MessagesSquare,
    Target,
    Timer,
    Award,
    ArrowDownRight,
} from "lucide-react";
import { Reveal, KineticText } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const IMG = {
    hero: "https://images.unsplash.com/photo-1544456203-0af5a69f5789?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    lecture:
        "https://images.unsplash.com/photo-1509062522246-3755977927d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    faculty:
        "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    mentor:
        "https://images.unsplash.com/photo-1601655781320-205e34c94eb1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const STATS = [
    { big: "Classes 6-12", small: "All subjects, all boards" },
    { big: "JEE · NEET", small: "Foundation tracks" },
    { big: "SSC · Banking", small: "Competitive exams" },
    { big: "Daily", small: "Doubt-clearing hours" },
];

const CHAPTERS = [
    {
        num: "01",
        title: "The Promise",
        img: IMG.lecture,
        body: "Every student who walks through our door gets seen, heard and taught personally. No one is a face in the crowd, no question is too small, and no student is written off. That is the promise we keep every single day.",
    },
    {
        num: "02",
        title: "The Method",
        img: IMG.faculty,
        body: "Concept, then practice, then test, then revise. A disciplined loop that turns understanding into marks. Weekly assessments keep progress visible while curated material keeps effort pointed in the right direction.",
    },
    {
        num: "03",
        title: "The Mentorship",
        img: IMG.mentor,
        body: "Our teachers do more than finish chapters. They track every student's progress, guide exam strategy, build temperament under pressure and stay available when doubt strikes — before the exam, not after it.",
    },
];

const COURSES = [
    {
        icon: BookOpenCheck,
        tag: "Foundation",
        title: "Classes 6-10 — All Subjects",
        body: "Concept-first teaching from day one, with weekly tests and structured doubt-clearing so fundamentals never wobble.",
    },
    {
        icon: Award,
        tag: "Senior Secondary",
        title: "Classes 11-12 — Science & Commerce",
        body: "Board-focused coverage with competitive-level depth, timed practice and chapter-wise revision cycles.",
    },
    {
        icon: Target,
        tag: "Engineering",
        title: "JEE Foundation & Preparation",
        body: "Physics, Chemistry and Mathematics built from basics up — problem drills, shortcut craft and exam temperament.",
    },
    {
        icon: BookOpenCheck,
        tag: "Medical",
        title: "NEET Foundation & Preparation",
        body: "Biology-first strategy with NCERT mastery, intensive MCQ practice and structured revision schedules.",
    },
    {
        icon: Timer,
        tag: "Competitive",
        title: "SSC & Banking Preparation",
        body: "Quant, reasoning, English and current affairs with sectional mock practice and speed-building sessions.",
    },
    {
        icon: MessagesSquare,
        tag: "Short Term",
        title: "Crash Courses & Doubt Support",
        body: "Fast-track revision batches before exams plus dedicated daily doubt hours for every enrolled student.",
    },
];

const WHY = [
    {
        icon: Users,
        title: "Small, focused batches",
        body: "Limited seats per batch so every student gets personal attention in every class.",
    },
    {
        icon: ClipboardCheck,
        title: "Weekly tests & reports",
        body: "Regular assessments with honest progress feedback shared with students and parents.",
    },
    {
        icon: MessagesSquare,
        title: "Daily doubt hours",
        body: "Dedicated time every day where no question goes unanswered and no concept stays unclear.",
    },
    {
        icon: Award,
        title: "Exam temperament",
        body: "Timed mocks and pressure-friendly practice so the real exam feels like just another test.",
    },
];

const Hero = () => {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 45, damping: 18 });
    const sy = useSpring(my, { stiffness: 45, damping: 18 });
    const orbX = useTransform(sx, (v) => v * 46);
    const orbY = useTransform(sy, (v) => v * 46);
    const orb2X = useTransform(sx, (v) => v * -30);
    const orb2Y = useTransform(sy, (v) => v * -30);

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

    const onMouseMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            ref={ref}
            onMouseMove={onMouseMove}
            className="relative overflow-hidden pt-36 sm:pt-40 pb-16 sm:pb-24"
            data-testid="hero-section"
        >
            <div className="hero-bg-grid absolute inset-0" aria-hidden="true" />
            <motion.div
                style={{ x: orbX, y: orbY }}
                className="absolute -top-20 -left-24 h-[420px] w-[420px] rounded-full bg-[#FF5500]/20 blur-[130px]"
                aria-hidden="true"
            />
            <motion.div
                style={{ x: orb2X, y: orb2Y }}
                className="absolute top-40 right-0 h-[360px] w-[360px] rounded-full bg-[#00F0FF]/10 blur-[120px]"
                aria-hidden="true"
            />

            <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="eyebrow mb-7"
                    >
                        Vivek Coaching Centre — Admissions Open
                    </motion.p>

                    <h1 className="f-display text-[clamp(2.9rem,8.2vw,6.6rem)] font-black uppercase leading-[0.95] tracking-tighter text-white">
                        <KineticText
                            delay={0.25}
                            lines={[
                                "Learning that",
                                "moves you",
                                <span key="a" className="f-serif normal-case font-medium text-[#FF5500]">
                                    ahead.
                                </span>,
                            ]}
                        />
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85 }}
                        className="mt-7 max-w-lg text-base sm:text-lg leading-relaxed text-slate-400"
                    >
                        Expert coaching for Classes 6-12, JEE &amp; NEET
                        foundation and competitive exams — with personal
                        attention, disciplined practice and results that speak
                        for themselves.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/contact"
                            data-testid="hero-admissions-cta"
                            className="btn-primary"
                        >
                            Start Admission Enquiry
                            <ArrowUpRight size={17} />
                        </Link>
                        <a
                            href="tel:+916207984279"
                            data-testid="hero-call-cta"
                            className="btn-outline"
                        >
                            <Phone size={16} className="text-[#FF5500]" />
                            Call 6207984279
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: imgY }}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="relative"
                >
                    <div className="img-frame img-spotlight h-[380px] sm:h-[480px]">
                        <img
                            src={IMG.hero}
                            alt="Focused student studying at Vivek Coaching Centre"
                            data-testid="hero-image"
                        />
                    </div>

                    <div
                        className="absolute -top-10 -right-6 sm:-right-10 h-28 w-28 sm:h-32 sm:w-32"
                        aria-hidden="true"
                    >
                        <svg viewBox="0 0 200 200" className="spin-slow h-full w-full">
                            <defs>
                                <path
                                    id="heroCircle"
                                    d="M100,100 m-80,0 a80,80 0 1,1 160,0 a80,80 0 1,1 -160,0"
                                />
                            </defs>
                            <text
                                fill="#94A3B8"
                                fontSize="12.5"
                                letterSpacing="4.5"
                                style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase" }}
                            >
                                <textPath href="#heroCircle">
                                    Vivek Coaching Centre · Admissions Open ·
                                    Vivek Coaching Centre · Admissions Open ·
                                </textPath>
                            </text>
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FF5500]">
                                <ArrowDownRight size={20} className="text-white" />
                            </div>
                        </div>
                    </div>

                    <div className="glass absolute -bottom-6 left-5 rounded-2xl px-5 py-4">
                        <p className="f-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                            Batch Size
                        </p>
                        <p className="f-display text-lg font-extrabold text-white">
                            Small &amp; Focused
                        </p>
                    </div>
                </motion.div>
            </div>

            <div className="relative mx-auto mt-20 max-w-6xl px-5 sm:px-8">
                <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 lg:grid-cols-4">
                    {STATS.map((s, i) => (
                        <Reveal
                            key={i}
                            delay={i * 0.08}
                            className="bg-[#0A0E16] px-6 py-7"
                        >
                            <p className="f-display text-xl sm:text-2xl font-extrabold tracking-tight text-white">
                                {s.big}
                            </p>
                            <p className="mt-1.5 f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                {s.small}
                            </p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};

const Manifesto = () => (
    <section className="py-24 sm:py-32" data-testid="manifesto-section">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <Reveal>
                <p className="eyebrow">Manifesto</p>
                <h2 className="mt-6 f-display text-3xl sm:text-5xl font-extrabold tracking-tight text-white max-w-2xl">
                    Discipline, engineered into results.
                </h2>
            </Reveal>

            <div className="mt-16 space-y-24">
                {CHAPTERS.map((c, i) => (
                    <div
                        key={c.num}
                        className={`grid items-center gap-10 lg:grid-cols-2 ${
                            i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                        }`}
                    >
                        <Reveal>
                            <div className="flex items-start gap-6">
                                <span className="chapter-num text-[5.5rem] sm:text-[7rem]">
                                    {c.num}
                                </span>
                                <div className="pt-3 sm:pt-5">
                                    <h3 className="f-display text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                                        {c.title}
                                    </h3>
                                    <p className="mt-4 max-w-md text-sm sm:text-base leading-relaxed text-slate-400">
                                        {c.body}
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="img-frame img-spotlight h-64 sm:h-80">
                                <img src={c.img} alt={c.title} loading="lazy" />
                            </div>
                        </Reveal>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const Courses = () => (
    <section className="py-24 sm:py-28 border-t border-white/5 bg-[#0A0E16]/60" data-testid="courses-section">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                <div>
                    <p className="eyebrow">Programmes</p>
                    <h2 className="mt-6 f-display text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
                        A batch for every ambition.
                    </h2>
                </div>
                <Link
                    to="/contact"
                    data-testid="courses-enquire-link"
                    className="nav-link w-fit"
                >
                    Enquire about admissions
                    <ArrowUpRight size={16} className="inline ml-1 text-[#FF5500]" />
                </Link>
            </Reveal>

            <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {COURSES.map((c, i) => (
                    <Reveal key={i} delay={(i % 3) * 0.08} className="h-full">
                        <div className="card-dark flex h-full flex-col p-7" data-testid={`course-card-${i + 1}`}>
                            <div className="flex items-center justify-between">
                                <c.icon size={26} className="text-[#FF5500]" />
                                <span className="chip">{c.tag}</span>
                            </div>
                            <h3 className="mt-6 f-display text-lg font-bold tracking-tight text-white">
                                {c.title}
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                {c.body}
                            </p>
                            <Link
                                to="/contact"
                                data-testid={`course-enquire-${i + 1}`}
                                className="mt-auto pt-6 text-sm font-semibold text-[#FF5500] hover:text-[#FF7A3D] transition-colors w-fit"
                            >
                                Enquire now
                                <ArrowUpRight size={15} className="inline ml-1" />
                            </Link>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const WhyUs = () => (
    <section className="py-24 sm:py-28" data-testid="why-us-section">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
            <Reveal>
                <p className="eyebrow">Why Choose Us</p>
                <h2 className="mt-6 f-display text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
                    The care of a mentor.
                    <span className="f-serif font-medium text-[#FF5500]">
                        {" "}
                        The rigour of a gymkhana.
                    </span>
                </h2>
                <p className="mt-6 max-w-md text-sm sm:text-base leading-relaxed text-slate-400">
                    We keep batches small, tests regular and doubt hours daily —
                    because steady, personal coaching beats crowded lecture
                    halls every time.
                </p>
            </Reveal>
            <div className="grid gap-5 sm:grid-cols-2">
                {WHY.map((w, i) => (
                    <Reveal key={i} delay={i * 0.08} className="h-full">
                        <div className="card-dark h-full p-7" data-testid={`why-card-${i + 1}`}>
                            <w.icon size={24} className="text-[#00F0FF]" />
                            <h3 className="mt-5 f-display text-base font-bold text-white">
                                {w.title}
                            </h3>
                            <p className="mt-2.5 text-sm leading-relaxed text-slate-400">
                                {w.body}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const CtaBand = () => (
    <section className="py-24 sm:py-32 border-t border-white/5 relative overflow-hidden" data-testid="cta-band">
        <div
            className="absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FF5500]/15 blur-[140px]"
            aria-hidden="true"
        />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 text-center">
            <Reveal>
                <h2 className="f-display text-[clamp(2.4rem,7vw,5.5rem)] font-black uppercase tracking-tighter text-white">
                    <KineticText
                        lines={[
                            "Ready to rise?",
                            <span key="a" className="f-serif normal-case font-medium text-[#FF5500]">
                                Admissions are open.
                            </span>,
                        ]}
                    />
                </h2>
                <p className="mx-auto mt-6 max-w-lg text-sm sm:text-base text-slate-400">
                    Call us, write to us or walk in — a short conversation is
                    all it takes to find the right batch for you.
                </p>
                <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                    <a href="tel:+916207984279" data-testid="cta-call-button" className="btn-primary">
                        <Phone size={16} />
                        Call 6207984279
                    </a>
                    <a href="mailto:zyne6431@gmail.com" data-testid="cta-email-button" className="btn-outline">
                        zyne6431@gmail.com
                    </a>
                </div>
            </Reveal>
        </div>
    </section>
);

const Home = () => (
    <main>
        <Hero />
        <Marquee />
        <Manifesto />
        <Courses />
        <WhyUs />
        <CtaBand />
    </main>
);

export default Home;
